import { TransitionLinkV2 } from "@/src/common/TransitionLinkV2";
import { browserStatics } from "@/src/shared/BrowserStatic";
import { urlFormat } from "@Components/Modules/Helpers";
import { BlogIndexMobile, BlogPostMobile } from "@Routes/BlogLinks";
import * as stylex from "@stylexjs/stylex";

import type { IBlogPost } from "./BlogPost";
import { BlogCloseIcon } from "./icons/BlogCloseIcon";
import { BlogNextIcon } from "./icons/BlogNextIcon";
import { BlogPreviousIcon } from "./icons/BlogPreviousIcon";

const styles = stylex.create({
    controls: {
        margin: 0,
        paddingBlock: 0,
        paddingInline: 0,
        backgroundColor: "#a9a9a9",
        height: "50px",
        width: "50px",
    },
    numbers: {
        transition: "background-color 0.3s ease",
        alignItems: "center",
        backgroundColor: {
            default: "rgba(0, 0, 0, 0.04)",
            ":hover": "rgba(0, 0, 0, 0.1)",
        },
        color: "black",
        display: "flex",
        justifyContent: "center",
    },

    isActive: {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
    },

    icons: {
        fill: "white",
        paddingBlock: "calc(50px * 0.2)",
        paddingInline: "0",
        transition: "padding 0.3s ease",
        display: "block",
        height: "50px",
        width: "50px",
    },
    blogNavigationComponent: {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
    },
    center: {
        textAlign: "center",
    },
    navigation: {
        width: "50%",
    },
    rightNavigation: {
        textAlign: "right",
    },
});

const getControlIcon = (
    url: string | undefined,
    RenderIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
    page: number | undefined,
): JSX.Element | null => {
    if (!url) {
        return null;
    }

    url = urlFormat(url, { page }, page > 1);

    return (
        <TransitionLinkV2 href={url} styleX={styles.controls} variant="none" isExternal>
            <RenderIcon {...stylex.props(styles.icons)} />
        </TransitionLinkV2>
    );
};

const getNavigationElement = (
    url: string | undefined,
    page: number,
    isActive: boolean = false,
): JSX.Element => {
    if (page && url) {
        url = urlFormat(url, { page }, page > 1);
    }

    return (
        <TransitionLinkV2
            variant="none"
            href={url}
            styleX={[styles.controls, styles.numbers, isActive && styles.isActive]}
        >
            <span>{page}</span>
        </TransitionLinkV2>
    );
};

export const blogNavigation = (
    urlTemplate: string,
    totalPages: number,
    currentPage: number,
): JSX.Element | null => {
    if (totalPages <= 1) {
        return null;
    }

    let prevIcon: JSX.Element | null = null;
    let nextIcon: JSX.Element | null = null;
    let prevPage: JSX.Element | null = null;
    let nextPage: JSX.Element | null = null;

    if (currentPage > 1) {
        const prevPageNumber = currentPage - 1;
        prevIcon = getControlIcon(urlTemplate, BlogPreviousIcon, prevPageNumber);
        prevPage = getNavigationElement(urlTemplate, prevPageNumber);
    }

    if (currentPage < totalPages) {
        const nextPageNumber = currentPage + 1;
        nextIcon = getControlIcon(urlTemplate, BlogNextIcon, nextPageNumber);
        nextPage = getNavigationElement(urlTemplate, nextPageNumber);
    }

    return (
        <div {...stylex.props(styles.blogNavigationComponent, styles.center)}>
            {prevIcon}
            {prevPage}
            {getNavigationElement(undefined, currentPage, true)}
            {nextPage}
            {nextIcon}
        </div>
    );
};

const BlogPost = { ...BlogPostMobile, url: "/blog/%7Bslug%7D/%7Bid%7D" };
const BlogIndex = "/blog";

export const postNavigation = (props: IBlogPost): JSX.Element => (
    <div {...stylex.props(styles.blogNavigationComponent)}>
        <div {...stylex.props(styles.navigation)}>
            {props.nextPage &&
                getControlIcon(
                    urlFormat(browserStatics.isMobileApp ? BlogPostMobile : BlogPost, props.nextPage),
                    BlogPreviousIcon,
                    undefined,
                )}
            {props.previousPage &&
                getControlIcon(
                    urlFormat(browserStatics.isMobileApp ? BlogPostMobile : BlogPost, props.previousPage),
                    BlogNextIcon,
                    undefined,
                )}
        </div>
        <div {...stylex.props(styles.navigation, styles.rightNavigation)}>
            {getControlIcon(
                browserStatics.isMobileApp ? BlogIndexMobile : BlogIndex,
                BlogCloseIcon,
                undefined,
            )}
        </div>
    </div>
);
